import type { components } from '@/api/schema';

import { labelOf } from './humanize';

type Schemas = components['schemas'];

export type ConsultationBranch = Schemas['ConsultationBranchInput']['branch'];
export type ConsultationDraftKind = Schemas['ConsultationDraftView']['kind'];
export type ClosingCallOutcome = Schemas['ClosingCallDecision']['outcome'];

/** The outcomes list of the manager report: each closing-call outcome, then the sales still waiting. */
export type ReportOutcome = ClosingCallOutcome | 'PENDING';

/** Where a branch or its task stands; the schema leaves both open. */
export type ConsultationBranchStatus =
  | 'TECHNICIAN_TASK_OPEN'
  | 'WORKFLOW_G_EXCEPTION_PENDING'
  | 'CONSENTED_REMINDER'
  | 'OUTREACH_SUPPRESSED'
  | 'RESOLVED'
  | 'SUPPRESSED';

/** Where an assistant draft stands; the schema leaves it open. */
export type ConsultationDraftStatus = 'DRAFT' | 'APPROVED' | 'REJECTED' | 'APPLIED';

/** The advisory contradictions the consultation detector raises; the schema leaves them open. */
export type ConsultationFlagCode =
  | 'MISSING_OBJECTIVE'
  | 'UNANSWERED_FIELD'
  | 'DECISION_MAKER_ABSENT'
  | 'NO_OPTIONS'
  | 'SCOPE_NOT_OFFERED'
  | 'SITE_NOT_EVIDENCED'
  | 'OPTIONS_ABOVE_STATED_BUDGET';

/** Why two customer records are treated as one person; the schema leaves it open. */
export type SuppressionMatchReason = 'NORMALIZED_EMAIL' | 'NORMALIZED_PHONE_AND_NAME';

/** Where a do-not-contact review stands; the schema leaves it open. */
export type SuppressionReviewStatus = 'REVIEW_REQUIRED' | 'CONFIRMED';

export const CONSULTATION_BRANCH_LABELS: Record<ConsultationBranch, string> = {
  TECHNICAL_REVIEW: 'Technical review',
  MANAGER_EXCEPTION: 'Manager exception',
  NEEDS_TIME: 'Customer needs time',
  DECLINES_FOLLOW_UP: 'Declines follow-up',
};

export const CONSULTATION_BRANCH_STATUS_LABELS: Record<ConsultationBranchStatus, string> = {
  TECHNICIAN_TASK_OPEN: 'Waiting for a technician',
  WORKFLOW_G_EXCEPTION_PENDING: 'Waiting for manager approval',
  CONSENTED_REMINDER: 'Reminder set with consent',
  OUTREACH_SUPPRESSED: 'Customer will not be contacted',
  RESOLVED: 'Resolved',
  SUPPRESSED: 'Closed — customer will not be contacted',
};

export const CONSULTATION_DRAFT_KIND_LABELS: Record<ConsultationDraftKind, string> = {
  DISCOVERY: 'Discovery notes',
  STATED_NEEDS: 'Stated needs',
  INTERNAL_NOTE: 'Internal note',
  FOLLOW_UP: 'Follow-up message',
  RECAP: 'Recap message',
};

export const CONSULTATION_DRAFT_STATUS_LABELS: Record<ConsultationDraftStatus, string> = {
  DRAFT: 'Draft',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  APPLIED: 'Applied',
};

export const CLOSING_CALL_OUTCOME_LABELS: Record<ClosingCallOutcome, string> = {
  ACCEPTED: 'Accepted',
  DECLINED: 'Declined',
  NO_DECISION: 'No decision',
};

export const REPORT_OUTCOME_LABELS: Record<ReportOutcome, string> = {
  ...CLOSING_CALL_OUTCOME_LABELS,
  PENDING: 'Waiting for a decision',
};

export const CONSULTATION_FLAG_LABELS: Record<ConsultationFlagCode, string> = {
  MISSING_OBJECTIVE: 'Stated needs not confirmed',
  UNANSWERED_FIELD: 'Checklist answer missing',
  DECISION_MAKER_ABSENT: 'Decision-maker not present',
  NO_OPTIONS: 'Nothing priced to present',
  SCOPE_NOT_OFFERED: 'Need not covered by any option',
  SITE_NOT_EVIDENCED: 'No matching equipment on record',
  OPTIONS_ABOVE_STATED_BUDGET: 'Options above the stated budget',
};

export const SUPPRESSION_MATCH_LABELS: Record<SuppressionMatchReason, string> = {
  NORMALIZED_EMAIL: 'Same email address',
  NORMALIZED_PHONE_AND_NAME: 'Same name and phone number',
};

export const SUPPRESSION_REVIEW_LABELS: Record<SuppressionReviewStatus, string> = {
  REVIEW_REQUIRED: 'Needs review',
  CONFIRMED: 'Confirmed',
};

export function consultationBranchLabel(branch: string | null | undefined): string {
  return labelOf(CONSULTATION_BRANCH_LABELS, branch);
}

export function consultationBranchStatusLabel(status: string | null | undefined): string {
  return labelOf(CONSULTATION_BRANCH_STATUS_LABELS, status);
}

export function consultationDraftKindLabel(kind: string | null | undefined): string {
  return labelOf(CONSULTATION_DRAFT_KIND_LABELS, kind);
}

export function consultationDraftStatusLabel(status: string | null | undefined): string {
  return labelOf(CONSULTATION_DRAFT_STATUS_LABELS, status);
}

export function closingCallOutcomeLabel(outcome: string | null | undefined): string {
  return labelOf(CLOSING_CALL_OUTCOME_LABELS, outcome);
}

export function reportOutcomeLabel(outcome: string | null | undefined): string {
  return labelOf(REPORT_OUTCOME_LABELS, outcome);
}

export function consultationFlagLabel(code: string | null | undefined): string {
  return labelOf(CONSULTATION_FLAG_LABELS, code);
}

export function suppressionMatchLabel(reason: string | null | undefined): string {
  return labelOf(SUPPRESSION_MATCH_LABELS, reason);
}

export function suppressionReviewLabel(status: string | null | undefined): string {
  return labelOf(SUPPRESSION_REVIEW_LABELS, status);
}
